import { useQuery, useQueryClient } from '@tanstack/react-query'
import api, {
    getWalletOrdinalsSnapshot,
    IOrdinal as TreechatOrdinal,
    type IOrdinalDescriptor,
    resolveOrdinalDescriptor,
} from 'api/api'
import useWalletDetails from 'api/useWalletDetails'
import { openCenter } from 'components/filters/useStreamFilters'
import OrdinalCard from './OrdinalCard'
import OrdinalsStrayFunds from './OrdinalsStrayFunds'
import MyOrdinalOrders from './MyOrdinalOrders'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useStore from 'state/knovStore'
import { subscribeOrdinalState } from 'state/ordinalRealtime'
import styles from './ordinals-viewer.module.scss'
import { useLocation, useNavigate } from 'react-router-dom'
import ResolvedOrdinalRender from 'components/answers/FileEmbed/ResolvedOrdinalRender'
import ResolvedOrdinalDescriptorRender from 'components/answers/FileEmbed/ResolvedOrdinalDescriptorRender'
import { useEventBus } from 'hooks/useEventBus'
import { EventBusPayload } from 'services/eventBusService'
import { filter } from 'rxjs'
import OrdinalDetails from './OrdinalDetails'
import SendOrdinal from './SendOrdinal'
import { useInView } from 'react-intersection-observer'
import type { CombinedOrdinal, OrdinalPreview } from 'types/ordinals'
import { shouldRetryWalletSnapshotQuery } from 'components/panels/ordinalStreamUpdatePolicy'

interface OrdinalsViewerProps {
    modalParams?: { type: string; ordinalId?: string; [key: string]: unknown }
    closeModal?: () => void
}

export type { CombinedOrdinal, OrdinalPreview } from 'types/ordinals'

const fetchOrdinalContent = async (contentUrl: string) => {
    try {
        const response = await fetch(contentUrl)
        if (!response.ok) {
            throw new Error(`Failed to fetch content: ${response.statusText}`)
        }
        const contentType = response.headers.get('content-type')
        if (contentType?.startsWith('text/')) {
            const text = await response.text()
            // Limit text length to prevent huge content from breaking the UI
            return text.slice(0, 100000)
        }
        return null
    } catch (error) {
        throw error
    }
}

const OrdinalsViewer: React.FC<OrdinalsViewerProps> = ({ modalParams, closeModal }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const currentUserBsvWalletType = useStore(state => state.currentUserBsvWalletType)
    const { data: walletDetailsData, isLoading: isInitialLoading } =
        useWalletDetails(currentUserBsvWalletType)
    const walletDetailsOrdinalsAddress = walletDetailsData?.ordinalsAddress

    const [selectedOrdinal, setSelectedOrdinal] = useState<OrdinalPreview | null>(null)
    const [sendTrayOpen, setSendTrayOpen] = useState(false)
    const [ordinalsView, setOrdinalsView] = useState<'regular' | 'profile' | 'hidden'>('regular')
    const [copiedAddress, setCopiedAddress] = useState(false)
    const activeModal = useStore(state => state.modalParams.type)
    const ordinalsModalIsOpen = activeModal === 'OrdinalsViewer'
    const debug = useStore(state => state.DEBUG)
    const lastCombinedOrdinalsDebugRef = useRef<string | null>(null)
    const queryClient = useQueryClient()
    const { getTopic$, isConnected: isEventBusConnected } = useEventBus()
    const ordinalsResolveManySubRef = useRef<any>(null)
    const galleryScrollRef = useRef<HTMLDivElement>(null)

    // ref to track if a treechat ordinals request is in progress
    const currentlyRefetchingTreechatOrds = useRef(false)

    // ref to track which external ordinalIds have already been processed by this comp
    const externallySelectedOrdinalIds = useRef<Set<string>>(new Set())

    const {
        data: walletSnapshot,
        isLoading: isWalletSnapshotLoading,
        isError: isWalletSnapshotError,
        refetch: refetchWalletSnapshot,
    } = useQuery({
        queryKey: ['walletOrdinalSnapshot', gon.currentUser?.id],
        queryFn: getWalletOrdinalsSnapshot,
        enabled: ordinalsModalIsOpen && !!gon.currentUser?.id,
        staleTime: 10 * 1000,
        retry: shouldRetryWalletSnapshotQuery,
        refetchInterval: query => {
            const snapshot = query.state.data
            if (!snapshot || snapshot.status !== 'fresh') return false
            const descriptors = snapshot.descriptors || []
            return descriptors.length < snapshot.outpoints.length ||
                descriptors.some(descriptor => ['pending', 'hydrating'].includes(descriptor.status))
                ? 3000
                : false
        },
    })

    const ordinalsAddress = walletSnapshot?.ordinals_address || walletDetailsOrdinalsAddress

    const walletSeenOutpoints = useMemo(
        () => Array.from(new Set((walletSnapshot?.outpoints || []).filter(Boolean))),
        [walletSnapshot?.outpoints],
    )

    const walletSeenOutpointsKey = useMemo(() => {
        if (!walletSeenOutpoints.length) return ''
        const sorted = [...walletSeenOutpoints].sort()
        return sorted.join(',')
    }, [walletSeenOutpoints])

    useEffect(() => {
        if (!debug || !ordinalsModalIsOpen) return
        const payload = {
            ordinalsAddress,
            count: walletSeenOutpoints.length,
            sample: walletSeenOutpoints.slice(0, 5),
        }
        console.debug('[OrdinalsViewer] walletSeenOutpoints', JSON.stringify(payload))
    }, [debug, ordinalsModalIsOpen, ordinalsAddress, walletSeenOutpointsKey])

    const resolveManyRes = walletSnapshot
    const isResolveManyLoading = isWalletSnapshotLoading
    const walletDescriptors = walletSnapshot?.descriptors || []
    const failedDescriptorCount = walletDescriptors.filter(
        descriptor => descriptor.status === 'error' || descriptor.ok === false,
    ).length
    const allWalletDescriptorsFailed =
        walletSeenOutpoints.length > 0 && failedDescriptorCount === walletSeenOutpoints.length
    const pendingDescriptorCount =
        walletDescriptors.filter(descriptor => ['pending', 'hydrating'].includes(descriptor.status))
            .length + Math.max(walletSeenOutpoints.length - walletDescriptors.length, 0)
    const walletDescriptorsPending = pendingDescriptorCount > 0
    const walletSnapshotIsFresh = walletSnapshot?.status === 'fresh'

    useEffect(() => {
        if (!debug || !ordinalsModalIsOpen) return
        const descriptors = resolveManyRes?.descriptors || []
        const stats = descriptors.reduce(
            (acc: Record<string, number>, d: IOrdinalDescriptor) => {
                const s = d?.status || 'missing'
                acc[s] = (acc[s] || 0) + 1
                if (!d?.origin_outpoint) acc.missing_origin_outpoint += 1
                if (d?.status === 'resolved' && !d?.content_url)
                    acc.resolved_missing_content_url += 1
                if (d?.status === 'error') acc.error_count += 1
                return acc
            },
            { missing_origin_outpoint: 0, resolved_missing_content_url: 0, error_count: 0 },
        )

        const payload = {
            isResolveManyLoading,
            ok: resolveManyRes?.ok,
            descriptorCount: descriptors.length,
            stats,
            sample: descriptors.slice(0, 5),
        }
        console.debug('[OrdinalsViewer] resolve_many result', JSON.stringify(payload))
        try {
            console.table(
                descriptors.slice(0, 20).map((d: IOrdinalDescriptor) => ({
                    outpoint: d?.outpoint,
                    status: d?.status,
                    origin_outpoint: d?.origin_outpoint,
                    bsv_ordinal_id: d?.bsv_ordinal_id,
                    has_content_url: !!d?.content_url,
                    content_type: d?.content_type,
                    error: d?.error,
                })),
            )
        } catch {}
    }, [debug, ordinalsModalIsOpen, isResolveManyLoading, resolveManyRes])

    // Refresh descriptors immediately when the resolver finishes any wallet outpoint.
    useEffect(() => {
        if (!ordinalsModalIsOpen || !walletSeenOutpoints.length || !isEventBusConnected) return
        const outpointSet = new Set(walletSeenOutpoints)

        ordinalsResolveManySubRef.current = getTopic$('ordinals.resolve')
            .pipe(
                filter((payload: EventBusPayload) => outpointSet.has(payload?.outpoint as string)),
            )
            .subscribe({
                next: () => {
                    if (debug) {
                        console.debug(
                            '[OrdinalsViewer] eventBus ordinals.resolve -> invalidate wallet snapshot',
                        )
                    }
                    queryClient.invalidateQueries({
                        queryKey: ['walletOrdinalSnapshot', gon.currentUser?.id],
                    })
                },
            })

        return () => {
            try {
                ordinalsResolveManySubRef.current?.unsubscribe?.()
            } catch {}
            ordinalsResolveManySubRef.current = null
        }
    }, [
        ordinalsModalIsOpen,
        walletSeenOutpointsKey,
        walletSeenOutpoints,
        isEventBusConnected,
        getTopic$,
        queryClient,
    ])

    const {
        data: treechatOrdinals,
        isLoading: isTreechatLoading,
        refetch: refetchTreechatOrdinals,
    } = useQuery({
        queryKey: ['treechatOrdinals', gon.currentUser?.id],
        queryFn: async () => {
            currentlyRefetchingTreechatOrds.current = true
            try {
                const result = await api.listOrdinals()
                return result?.ordinals
            } finally {
                currentlyRefetchingTreechatOrds.current = false
            }
        },
        // No polling: only fetch when modal opens, or when we explicitly refetch (e.g. after send).
        enabled: ordinalsModalIsOpen && !!gon.currentUser?.id,
    })

    const isLoading =
        (isInitialLoading && !walletSnapshot) || isWalletSnapshotLoading || isTreechatLoading

    // fn to safely refetch treechat ordinals if not already fetching
    const safeRefetchTreechatOrds = () => {
        if (!currentlyRefetchingTreechatOrds.current) {
            refetchTreechatOrdinals()
        }
    }

    // Trigger refetch when modal is shown or interacted with
    useEffect(() => {
        if (ordinalsModalIsOpen) {
            refetchWalletSnapshot()
            safeRefetchTreechatOrds()
        }
    }, [ordinalsModalIsOpen])

    // Add event listeners for user interaction with the modal
    // NOTE: We intentionally avoid refetching on every click/scroll; Treechat ordinals already
    // refetch on an interval and on modal open. Interaction-driven refetching caused constant
    // rerenders and duplication-like UI thrash.

    // Wallet-driven ordinals list:
    // - cards come ONLY from wallet UTXO outpoints (via resolve_many descriptors)
    // - Treechat data can *enrich* an existing wallet item but never creates additional cards
    // - Exception: show Treechat "pending mint" ordinals (no origin yet) so a mint can be opened
    // - Exception: also show recent Treechat mints and settled purchases while the wallet index catches up
    const combinedOrdinals = useMemo(() => {
        const ordinalMap = new Map<string, CombinedOrdinal>()

        const treechatByOrigin = new Map<string, TreechatOrdinal>()
        const treechatPendingNoOrigin: TreechatOrdinal[] = []
        if (Array.isArray(treechatOrdinals)) {
            treechatOrdinals.forEach(treeOrd => {
                const origin = String(treeOrd?.origin || '').trim()
                if (!origin || !/^[0-9a-f]{64}_[0-9]+$/i.test(origin)) {
                    // Newly minted ordinals can exist in our DB before `origin` is set (minting/confirming).
                    // Keep them visible in the UI until the wallet index catches up.
                    treechatPendingNoOrigin.push(treeOrd)
                    return
                }
                treechatByOrigin.set(origin, treeOrd)
            })
        }

        // Wallet ordinals: render ONLY from our Rails descriptors (resolve_many).
        const descriptors = resolveManyRes?.descriptors || []
        if (Array.isArray(descriptors)) {
            descriptors.forEach(desc => {
                if (!desc || desc.status !== 'resolved') return
                const originOutpoint = String(desc.origin_outpoint || '').trim()
                const seenOutpoint = String(desc.outpoint || '').trim()
                if (!originOutpoint) return

                const [txid, voutStr] = originOutpoint.split('_')
                const voutNum = Number(voutStr)

                const treechatData = treechatByOrigin.get(originOutpoint)

                ordinalMap.set(originOutpoint, {
                    txid,
                    vout: Number.isFinite(voutNum) ? voutNum : 0,
                    outpoint: originOutpoint,
                    utxo_outpoint: seenOutpoint,
                    inscription_outpoint: originOutpoint,
                    origin: {
                        outpoint: originOutpoint,
                        data: {
                            insc: {
                                file: {
                                    type:
                                        desc.content_type ||
                                        treechatData?.content_type ||
                                        'application/octet-stream',
                                },
                            },
                            map: {
                                name: originOutpoint,
                            },
                        },
                    },
                    treechatData,
                    status: 'confirmed' as const,
                    descriptor: desc,
                })
            })
        }

        // Newly minted or purchased Treechat ordinals can have an origin before they appear in the wallet
        // unspent index (e.g. mempool/unconfirmed, third-party index lag). Keep those explicit backend-owned
        // fallbacks visible as unconfirmed until the wallet snapshot contains them.
        //
        // IMPORTANT: keep this narrowly scoped to avoid showing stale "DB-owned" ordinals that were sent away.
        const nowMs = Date.now()
        const RECENT_MINT_WINDOW_MS = 24 * 60 * 60 * 1000
        const VERY_RECENT_WINDOW_MS = 15 * 60 * 1000

        treechatByOrigin.forEach((treeOrd, originOutpoint) => {
            if (ordinalMap.has(originOutpoint)) return

            const mintedByTreechat = treeOrd?.minted_by_treechat === true
            const recentMarketPurchase = treeOrd?.recent_market_purchase === true
            if (!mintedByTreechat && !recentMarketPurchase) return

            const createdAt = String(treeOrd?.created_at || '').trim()
            const createdMs = createdAt ? Date.parse(createdAt) : NaN
            const ageMs = Number.isFinite(createdMs) ? nowMs - createdMs : Number.POSITIVE_INFINITY
            if (!recentMarketPurchase) {
                if (!(ageMs >= 0 && ageMs <= RECENT_MINT_WINDOW_MS)) return

                const hasMintTxId = !!String(treeOrd?.mint_tx_id || '').trim()
                // Allow a brief window for the backend to attach `mint_tx_id`.
                if (!hasMintTxId && ageMs > VERY_RECENT_WINDOW_MS) return
            }

            const [txid, voutStr] = originOutpoint.split('_')
            const voutNum = Number(voutStr)
            ordinalMap.set(originOutpoint, {
                txid,
                vout: Number.isFinite(voutNum) ? voutNum : 0,
                outpoint: originOutpoint,
                utxo_outpoint: originOutpoint,
                inscription_outpoint: originOutpoint,
                origin: {
                    outpoint: originOutpoint,
                    data: {
                        insc: {
                            file: {
                                type: treeOrd?.content_type || 'application/octet-stream',
                            },
                        },
                        map: {
                            name: originOutpoint,
                        },
                    },
                },
                treechatData: treeOrd,
                status: 'unconfirmed' as const,
            })
        })

        // Always show Treechat ordinals that are actively minting/confirming (origin not yet set).
        treechatPendingNoOrigin.forEach(treeOrd => {
            const id = String(treeOrd?.id || '').trim()
            if (!id) return
            const key = `treechat:${id}`
            if (ordinalMap.has(key)) return
            ordinalMap.set(key, {
                txid: '',
                vout: 0,
                outpoint: key,
                utxo_outpoint: key,
                inscription_outpoint: '',
                origin: {
                    outpoint: '',
                    data: {
                        insc: {
                            file: {
                                type: treeOrd?.content_type || 'application/octet-stream',
                            },
                        },
                        map: {
                            name: `Minting… (${id})`,
                        },
                    },
                },
                treechatData: treeOrd,
                status: 'pending' as const,
            })
        })

        // Sort by Treechat created_at when present, otherwise stable by outpoint.
        return Array.from(ordinalMap.values()).sort((a, b) => {
            if (a.status === 'unconfirmed' && b.status !== 'unconfirmed') return -1
            if (a.status !== 'unconfirmed' && b.status === 'unconfirmed') return 1
            const aDate = a.treechatData?.created_at || ''
            const bDate = b.treechatData?.created_at || ''
            if (aDate && bDate) return bDate.localeCompare(aDate)
            if (aDate && !bDate) return -1
            if (!aDate && bDate) return 1
            return String(a.outpoint).localeCompare(String(b.outpoint))
        })
    }, [resolveManyRes, treechatOrdinals, debug])

    // A pending purchase used to poll the wallet snapshot every 10s here. The
    // server now detects the flip (Ordinals::PurchaseConfirmationWorker) and
    // publishes it, so this waits for that event instead of asking repeatedly.
    useEffect(() => {
        if (!ordinalsModalIsOpen || !combinedOrdinals.some(o => o.status === 'unconfirmed')) return

        return subscribeOrdinalState(() => {
            refetchWalletSnapshot()
        })
    }, [combinedOrdinals, ordinalsModalIsOpen, refetchWalletSnapshot])

    useEffect(() => {
        if (!debug || !ordinalsModalIsOpen) return
        const total = combinedOrdinals.length
        const missingDescriptor = combinedOrdinals.filter(o => !o.descriptor && !o.treechatData)
        const resolvedNoUrl = combinedOrdinals.filter(
            o => o.descriptor?.status === 'resolved' && !o.descriptor?.content_url,
        )
        const payload = {
            total,
            missingDescriptorCount: missingDescriptor.length,
            resolvedNoUrlCount: resolvedNoUrl.length,
            sampleMissing: missingDescriptor.slice(0, 3).map(o => o.outpoint),
            sampleResolvedNoUrl: resolvedNoUrl.slice(0, 3).map(o => o.descriptor?.outpoint),
        }
        const msg = JSON.stringify(payload)
        if (lastCombinedOrdinalsDebugRef.current === msg) return
        lastCombinedOrdinalsDebugRef.current = msg
        console.debug('[OrdinalsViewer] combinedOrdinals', msg)
    }, [debug, ordinalsModalIsOpen, combinedOrdinals])

    // handle externally selected ordinal by ordinalid
    useEffect(() => {
        if (ordinalsModalIsOpen && modalParams?.ordinalId && combinedOrdinals.length > 0) {
            // only process this ordinalid if we haven't seen it before
            if (!externallySelectedOrdinalIds.current.has(modalParams.ordinalId)) {
                // find the ordinal with the matching origin/outpoint
                const targetOrdinal = combinedOrdinals.find(
                    ordinal =>
                        getInscriptionOutpoint(ordinal) === modalParams.ordinalId ||
                        ordinal.outpoint === modalParams.ordinalId,
                )

                if (targetOrdinal) {
                    // if found, set it as the selected ordinal
                    setSelectedOrdinal({
                        ordinal: targetOrdinal,
                        content: null,
                        isLoading: false,
                        forceLive: false,
                    })

                    // mark that this external ordinal has already been opened once
                    externallySelectedOrdinalIds.current.add(modalParams.ordinalId)
                }
            }
        }
    }, [ordinalsModalIsOpen, modalParams?.ordinalId, combinedOrdinals])

    // reset processed ordinalids when modal is closed
    useEffect(() => {
        if (!ordinalsModalIsOpen) {
            externallySelectedOrdinalIds.current.clear()
        }
    }, [ordinalsModalIsOpen])

    // Update URL when selected ordinal changes
    useEffect(() => {
        if (selectedOrdinal && ordinalsModalIsOpen) {
            const newUrl = `/modal/ordinals/${getInscriptionOutpoint(selectedOrdinal.ordinal)}`
            // Only update if URL is different to avoid unnecessary history entries
            if (location.pathname !== newUrl) {
                window.history.replaceState({}, '', newUrl)
            }
        }
    }, [selectedOrdinal, ordinalsModalIsOpen, location.pathname])

    const handleOrdinalClick = async (
        ordinal: CombinedOrdinal,
        options: { forceLive?: boolean } = {},
    ) => {
        const fileType = ordinal.origin?.data?.insc?.file?.type || ''
        const inscriptionOutpoint = getInscriptionOutpoint(ordinal)
        const forceLive = !!options.forceLive
        if (fileType.toLowerCase().startsWith('text/html')) {
            // For HTML content, show in iframe without fetching text
            setSelectedOrdinal({ ordinal, content: null, isLoading: false, forceLive })
        } else if (fileType.toLowerCase().startsWith('text/')) {
            // Set loading state immediately
            setSelectedOrdinal({ ordinal, content: null, isLoading: true, forceLive })
            try {
                // Backend-first: never fetch ordinal bytes directly from third-party hosts.
                // Wait for Rails to provide a content_url via the resolver pipeline.
                let contentUrl: string | null = null
                try {
                    // Resolve using the *seen UTXO outpoint* so backend can cache bsv_vouts mappings.
                    const seen = ordinal.utxo_outpoint || ordinal.outpoint
                    const descRes = await resolveOrdinalDescriptor(seen, {
                        assumeOrigin: false,
                        originOutpointHint: inscriptionOutpoint,
                    })
                    if (descRes?.status === 'resolved' && descRes.content_url)
                        contentUrl = descRes.content_url
                } catch (e) {}

                if (!contentUrl) {
                    setSelectedOrdinal({
                        ordinal,
                        content: null,
                        isLoading: false,
                        error: 'Ordinal is still resolving. Try again in a moment.',
                        forceLive,
                    })
                    return
                }

                const content = await fetchOrdinalContent(contentUrl)
                setSelectedOrdinal({ ordinal, content, isLoading: false, forceLive })
            } catch (error) {
                setSelectedOrdinal({
                    ordinal,
                    content: null,
                    isLoading: false,
                    error: error instanceof Error ? error.message : 'Failed to load content',
                    forceLive,
                })
            }
        } else {
            setSelectedOrdinal({ ordinal, content: null, forceLive })
        }
    }

    const closeLightbox = () => {
        setSelectedOrdinal(null)
        setSendTrayOpen(false)
        window.history.replaceState({}, '', '/modal/ordinals')
    }

    const handleLightboxClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) closeLightbox()
    }

    useEffect(() => {
        // If the user selects a different ordinal, default back to details view
        setSendTrayOpen(false)
    }, [selectedOrdinal?.ordinal?.outpoint])

    const fallbackCopyTextToClipboard = (text: string) => {
        try {
            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.setAttribute('readonly', '')
            textarea.style.position = 'fixed'
            textarea.style.top = '0'
            textarea.style.left = '0'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.focus()
            textarea.select()
            const ok = document.execCommand('copy')
            document.body.removeChild(textarea)
            return ok
        } catch (error) {
            console.warn('Fallback clipboard copy failed.', error)
            return false
        }
    }

    const copyAddress = () => {
        if (!ordinalsAddress) return

        const done = () => {
            setCopiedAddress(true)
            window.setTimeout(() => setCopiedAddress(false), 2000)
        }

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard
                .writeText(ordinalsAddress)
                .then(done)
                .catch(error => {
                    console.warn('Clipboard API writeText failed, falling back.', error)
                    fallbackCopyTextToClipboard(ordinalsAddress)
                        ? done()
                        : alert('Failed to copy to clipboard')
                })
            return
        }

        fallbackCopyTextToClipboard(ordinalsAddress) ? done() : alert('Failed to copy to clipboard')
    }

    const getInscriptionOutpoint = (ordinal: CombinedOrdinal): string => {
        // Treechat ordinals: `treechatData.origin` is the inscription origin outpoint (stored in BsvOrdinal.origin)
        const treechatOrigin = ordinal.treechatData?.origin
        if (treechatOrigin && /^[0-9a-f]{64}_[0-9]+$/i.test(String(treechatOrigin)))
            return treechatOrigin

        // GorillaPool ordinals: response includes `origin.outpoint` (inscription origin), while `outpoint` is current UTXO
        const gpOrigin = ordinal.origin?.outpoint
        if (gpOrigin && /^[0-9a-f]{64}_[0-9]+$/i.test(String(gpOrigin))) return gpOrigin

        if (ordinal.inscription_outpoint) return ordinal.inscription_outpoint

        // Fallback: use current outpoint if origin is unavailable
        return ordinal.outpoint
    }

    const isHiddenOrdinal = (ordinal: CombinedOrdinal) => {
        return !!(
            ordinal.hidden ||
            ordinal.users_ordinal?.hidden ||
            ordinal.treechatData?.users_ordinal?.hidden
        )
    }

    const isShownInProfileOrdinal = (ordinal: CombinedOrdinal) => {
        return !!(
            ordinal.users_ordinal?.show_in_profile ||
            ordinal.treechatData?.users_ordinal?.show_in_profile
        )
    }

    const { filteredOrdinals, regularCount, profileCount, hiddenCount } = useMemo(() => {
        const hidden = combinedOrdinals.filter(isHiddenOrdinal)
        const regular = combinedOrdinals.filter(o => !isHiddenOrdinal(o))
        const profile = regular.filter(isShownInProfileOrdinal)
        return {
            filteredOrdinals:
                ordinalsView === 'hidden' ? hidden : ordinalsView === 'profile' ? profile : regular,
            regularCount: regular.length,
            profileCount: profile.length,
            hiddenCount: hidden.length,
        }
    }, [combinedOrdinals, ordinalsView])

    const isGltfType = (ct?: string | null) => {
        const t = String(ct || '').toLowerCase()
        return (
            t.includes('model/gltf-binary') ||
            t.includes('model/gltf+json') ||
            t.includes('model/gltf')
        )
    }

    const OrdinalPreviewInView = ({ ordinal }: { ordinal: CombinedOrdinal }) => {
        const { ref, inView } = useInView({
            root: null,
            rootMargin: '500px 0px 500px 0px',
            threshold: 0,
        })

        const fileType = ordinal.origin?.data?.insc?.file?.type || ''
        const inscriptionOutpoint = getInscriptionOutpoint(ordinal)
        const inscriptionLooksValid = /^[0-9a-f]{64}_[0-9]+$/i.test(
            String(inscriptionOutpoint || ''),
        )

        const knownPreviewUrl =
            ordinal.descriptor?.preview_url || ordinal.treechatData?.preview_url || null

        const autoCapture3dPreview = inView && isGltfType(fileType) && !knownPreviewUrl

        // If we have a resolved descriptor (Rails), prefer rendering directly from it (no per-card resolve calls).
        if (ordinal.descriptor && ordinal.descriptor.status === 'resolved') {
            return (
                <div ref={ref} className={styles.preview}>
                    <ResolvedOrdinalDescriptorRender
                        descriptor={ordinal.descriptor}
                        allowInteraction={false}
                        autoCapture3dPreview={autoCapture3dPreview}
                        onRequestLive={() => handleOrdinalClick(ordinal, { forceLive: true })}
                        // Wallet gallery tiles: prefer our cached preview variants
                        htmlAlign="top-center"
                        fillVideoContainer
                    />
                </div>
            )
        }

        return (
            <div ref={ref} className={styles.preview}>
                <ResolvedOrdinalRender
                    treechatOrdinalId={ordinal.treechatData?.id}
                    treechatPreviewUrl={ordinal.treechatData?.preview_url}
                    seenOutpoint={
                        inscriptionLooksValid ? ordinal.utxo_outpoint || inscriptionOutpoint : ''
                    }
                    originOutpointHint={inscriptionLooksValid ? inscriptionOutpoint : undefined}
                    assumeOrigin={false}
                    fileType={fileType}
                    allowInteraction={false}
                    autoCapture3dPreview={autoCapture3dPreview}
                    onRequestLive={() => handleOrdinalClick(ordinal, { forceLive: true })}
                    htmlAlign="top-center"
                    fillVideoContainer
                />
            </div>
        )
    }

    const renderPreview = (ordinal: CombinedOrdinal) => {
        return <OrdinalPreviewInView ordinal={ordinal} />
    }

    return (
        <div className={styles.container} data-testid="my-ordinals-viewer">
            <div className={styles.header}>
                <h1 className={styles.title}>
                    {isLoading ? 'Loading Ordinals...' : 'Manage Ordinals'}
                </h1>
                {!isLoading && ordinalsAddress && (
                    <div className={styles.addressBadge}>
                        <span className={styles.addressText}>{ordinalsAddress}</span>
                        <button
                            type="button"
                            className={`${styles.copyButton} ${
                                copiedAddress ? styles.copyButtonCopied : ''
                            }`}
                            onClick={copyAddress}
                            title={copiedAddress ? 'Copied!' : 'Copy Address'}
                            aria-label={copiedAddress ? 'Address copied' : 'Copy Address'}
                        >
                            <i className={`fa ${copiedAddress ? 'fa-check' : 'fa-clone'}`} />
                        </button>
                    </div>
                )}

                {!isLoading && (
                    <div className={styles.viewSwitch}>
                        <button
                            type="button"
                            className={`${styles.viewSwitchButton} ${
                                ordinalsView === 'regular' ? styles.active : ''
                            }`}
                            onClick={() => setOrdinalsView('regular')}
                            aria-pressed={ordinalsView === 'regular'}
                        >
                            <span>Ordinals</span>
                            <span className={styles.viewCount}>{regularCount}</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.viewSwitchButton} ${
                                ordinalsView === 'profile' ? styles.active : ''
                            }`}
                            onClick={() => setOrdinalsView('profile')}
                            aria-pressed={ordinalsView === 'profile'}
                        >
                            <span>Profile</span>
                            <span className={styles.viewCount}>{profileCount}</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.viewSwitchButton} ${
                                ordinalsView === 'hidden' ? styles.active : ''
                            }`}
                            onClick={() => setOrdinalsView('hidden')}
                            aria-pressed={ordinalsView === 'hidden'}
                        >
                            <span>Hidden</span>
                            <span className={styles.viewCount}>{hiddenCount}</span>
                        </button>
                    </div>
                )}
            </div>

            <div ref={galleryScrollRef} className={styles.scrollableContent}>
                <OrdinalsStrayFunds enabled={ordinalsModalIsOpen} className={styles.strayFunds} />
                <MyOrdinalOrders enabled={ordinalsModalIsOpen} className={styles.strayFunds} />
                {walletSnapshot?.status === 'stale' && (
                    <div className={styles.snapshotWarning} role="status">
                        Showing the last successful wallet update. Live wallet data is temporarily
                        unavailable.
                        <button type="button" onClick={() => refetchWalletSnapshot()}>
                            Retry
                        </button>
                    </div>
                )}
                {walletSnapshot?.status === 'partial' && (
                    <div className={styles.snapshotWarning} role="status">
                        This wallet update is incomplete. Some ordinals may be missing while live
                        data is being refreshed.
                    </div>
                )}
                {failedDescriptorCount > 0 && !allWalletDescriptorsFailed && (
                    <div className={styles.snapshotWarning} role="status">
                        {failedDescriptorCount}{' '}
                        {failedDescriptorCount === 1 ? 'ordinal is' : 'ordinals are'} still unable
                        to resolve.
                        <button type="button" onClick={() => refetchWalletSnapshot()}>
                            Retry
                        </button>
                    </div>
                )}
                {pendingDescriptorCount > 0 && combinedOrdinals.length > 0 && (
                    <div className={styles.snapshotWarning} role="status">
                        {walletSnapshotIsFresh ? 'Resolving' : 'The last wallet update includes'}{' '}
                        {pendingDescriptorCount} more{' '}
                        {pendingDescriptorCount === 1 ? 'ordinal' : 'ordinals'}
                        {walletSnapshotIsFresh ? '…' : ' that are not resolved yet.'}
                    </div>
                )}
                {isLoading ? (
                    <div className={styles.loading}>
                        <i className="fa fa-spinner fa-spin" />
                    </div>
                ) : isWalletSnapshotError ? (
                    <div className={styles.snapshotError} role="alert">
                        <i className="fa fa-exclamation-triangle" />
                        <p>We couldn’t refresh this wallet’s ordinals.</p>
                        <button type="button" onClick={() => refetchWalletSnapshot()}>
                            Try again
                        </button>
                    </div>
                ) : allWalletDescriptorsFailed ? (
                    <div className={styles.snapshotError} role="alert">
                        <i className="fa fa-exclamation-triangle" />
                        <p>We found this wallet’s ordinals, but couldn’t resolve their content.</p>
                        <button type="button" onClick={() => refetchWalletSnapshot()}>
                            Try again
                        </button>
                    </div>
                ) : !walletSnapshotIsFresh &&
                  walletSeenOutpoints.length > 0 &&
                  walletDescriptorsPending &&
                  combinedOrdinals.length === 0 ? (
                    <div className={styles.snapshotError} role="alert">
                        <i className="fa fa-exclamation-triangle" />
                        <p>The last wallet update contains ordinals that aren’t resolved yet.</p>
                        <button type="button" onClick={() => refetchWalletSnapshot()}>
                            Retry live update
                        </button>
                    </div>
                ) : walletSeenOutpoints.length > 0 &&
                  walletSnapshotIsFresh &&
                  walletDescriptorsPending &&
                  combinedOrdinals.length === 0 ? (
                    <div className={styles.loading}>Resolving ordinals…</div>
                ) : filteredOrdinals.length > 0 ? (
                    <>
                        <div className={styles.gallery}>
                            {filteredOrdinals
                                .filter((ordinal: CombinedOrdinal) => {
                                    // Only render cards that have an origin inscription we can anchor to.
                                    // Wallet ordinals should come from resolve_many (descriptor), Treechat ordinals have treechatData.
                                    const hasTreechat =
                                        !!ordinal.treechatData &&
                                        !!String(ordinal.treechatData?.id || '').trim()
                                    const hasResolvedDescriptor =
                                        !!ordinal.descriptor &&
                                        ordinal.descriptor.status === 'resolved' &&
                                        !!ordinal.descriptor.origin_outpoint
                                    return hasTreechat || hasResolvedDescriptor
                                })
                                .map((ordinal: CombinedOrdinal) => (
                                    <OrdinalCard
                                        key={
                                            String(ordinal.treechatData?.id || '').trim()
                                                ? `treechat:${String(ordinal.treechatData?.id)}`
                                                : getInscriptionOutpoint(ordinal)
                                        }
                                        ordinal={ordinal}
                                        renderPreview={(o: CombinedOrdinal) => renderPreview(o)}
                                        onClick={(o: CombinedOrdinal) => handleOrdinalClick(o)}
                                        onTreechatClick={(o: CombinedOrdinal) => {
                                            if (
                                                o.treechatData &&
                                                o.status === 'confirmed' &&
                                                closeModal
                                            ) {
                                                closeModal()
                                                const { quest_id, answer_id } = o.treechatData || {}
                                                if (quest_id && answer_id) {
                                                    openCenter({
                                                        questId: quest_id,
                                                        answerId: answer_id,
                                                    })
                                                }
                                            }
                                        }}
                                    />
                                ))}
                        </div>
                    </>
                ) : (
                    !isLoading && (
                        <div className={styles.emptyState} data-testid="my-ordinals-empty-state">
                            <i className="fa fa-diamond" />
                            <p>
                                {ordinalsView === 'hidden'
                                    ? 'No hidden ordinals yet'
                                    : ordinalsView === 'profile'
                                    ? 'No ordinals set to show on your profile yet'
                                    : 'No ordinals found in this wallet'}
                            </p>
                        </div>
                    )
                )}
            </div>

            {selectedOrdinal && (
                <div className={styles.lightbox} onClick={handleLightboxClick}>
                    <OrdinalDetails
                        selectedOrdinal={selectedOrdinal}
                        inscriptionOutpoint={getInscriptionOutpoint(selectedOrdinal.ordinal)}
                        onClose={closeLightbox}
                        onSendClick={() => setSendTrayOpen(true)}
                    />

                    {sendTrayOpen && (
                        <SendOrdinal
                            ordinal={selectedOrdinal.ordinal}
                            onDismiss={() => setSendTrayOpen(false)}
                            onRefreshOrdinals={() => {
                                refetchTreechatOrdinals()
                                refetchWalletSnapshot()
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default OrdinalsViewer
